import * as THREE from 'three';
import { CONFIG } from '../config.js';
import { applyTheme, setPageTitle } from './setup.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';


let isEditorActive = false;
let raycaster, camera, scene, hotspotsArray, renderer, orbitControls, transformControls;
let capturedPosition = null;

// Temporary storage for new items
const newHotspots = [];
const newPanels = {};

// Local copy of config for editing
let editConfig = JSON.parse(JSON.stringify(CONFIG));

/**
 * Initialize the Editor Module
 */
export function initEditor(threeRaycaster, threeCamera, threeScene, existingHotspots, threeRenderer, threeControls, threeTransform) {
    if (!CONFIG.settings.enableEditor) return;

    // Store references
    raycaster = threeRaycaster;
    camera = threeCamera;
    scene = threeScene;
    hotspotsArray = existingHotspots;
    renderer = threeRenderer;
    orbitControls = threeControls;
    transformControls = threeTransform;

    // Show UI Toggle
    document.getElementById('editor-ui').style.display = 'block';

    // Event Listeners
    document.getElementById('toggle-editor-btn').addEventListener('click', toggleEditorMode);
    document.getElementById('save-hotspot-btn').addEventListener('click', saveNewHotspot);
    document.getElementById('cancel-hotspot-btn').addEventListener('click', closeCreatorModal);

    document.getElementById('export-config-btn').addEventListener('click', showExportCode);
    document.getElementById('close-export-btn').addEventListener('click', () => {
        document.getElementById('export-modal').classList.remove('active');
    });
    document.getElementById('copy-export-btn').addEventListener('click', copyExportCode);

    // Sidebar Listeners
    setupSidebarListeners();
}

/**
 * Setup Sidebar Inputs and Live Preview
 */
function setupSidebarListeners() {
    // Fill Initial Values
    if (CONFIG.theme.backgroundGradient) {
        document.getElementById('edit-bg-top').value = CONFIG.theme.backgroundGradient.top;
        document.getElementById('edit-bg-bottom').value = CONFIG.theme.backgroundGradient.bottom;
    }
    document.getElementById('edit-nav-color').value = CONFIG.theme.navBar.backgroundColor;
    // We assume hex for color input, might need conversion if config uses names
    // For now we assume the setup wizard used hex

    document.getElementById('edit-model-path').value = CONFIG.model.path;
    document.getElementById('edit-page-title').value = CONFIG.settings.pageTitle;

    // --- Live Update Listeners ---

    // Theme Colors
    const updateTheme = () => {
        CONFIG.theme.backgroundGradient.top = document.getElementById('edit-bg-top').value;
        CONFIG.theme.backgroundGradient.bottom = document.getElementById('edit-bg-bottom').value;
        CONFIG.theme.navBar.backgroundColor = document.getElementById('edit-nav-color').value;

        // This is a bit of a hack: direct DOM manipulation for instant feedback
        applyTheme();
    };

    document.getElementById('edit-bg-top').addEventListener('input', updateTheme);
    document.getElementById('edit-bg-bottom').addEventListener('input', updateTheme);
    document.getElementById('edit-nav-color').addEventListener('input', updateTheme);

    // Camera Coords
    document.getElementById('set-cam-current').addEventListener('click', () => {
        const x = Math.round(camera.position.x);
        const y = Math.round(camera.position.y);
        const z = Math.round(camera.position.z);

        document.getElementById('cam-coord-display').textContent = `X: ${x}, Y: ${y}, Z: ${z}`;

        // Update local config
        editConfig.model.cameraPosition = { x, y, z };

        // Flash success
        const btn = document.getElementById('set-cam-current');
        const originalText = btn.textContent;
        btn.textContent = '✅ Captured!';
        setTimeout(() => btn.textContent = originalText, 1000);
    });

    // Page Title
    document.getElementById('edit-page-title').addEventListener('input', (e) => {
        CONFIG.settings.pageTitle = e.target.value;
        setPageTitle();
    });

    // Model Reload
    document.getElementById('reload-model-btn').addEventListener('click', () => {
        const newPath = document.getElementById('edit-model-path').value;
        if (newPath && newPath !== CONFIG.model.path) {
            alert('Reloading model requires a page refresh. Please update config.js manually for model path changes to take permanent effect.');
            // Creating a true hot-swap for the model is complex due to scene graph cleanup.
            // For now, valid feedback is better than broken logic.
            CONFIG.model.path = newPath;
        }
    });

    // Plexus Toggle
    const plexusCheck = document.getElementById('edit-show-plexus');
    if (plexusCheck) {
        plexusCheck.checked = CONFIG.settings.showPlexus !== false;
        plexusCheck.addEventListener('change', (e) => {
            CONFIG.settings.showPlexus = e.target.checked;
            window.dispatchEvent(new CustomEvent('toggle-plexus', { detail: { visible: e.target.checked } }));
        });
    }
}

/**
 * Toggle Creator Mode
 */
function toggleEditorMode() {
    isEditorActive = !isEditorActive;
    const btn = document.getElementById('toggle-editor-btn');
    const sidebar = document.getElementById('editor-sidebar');

    if (isEditorActive) {
        if (window.SceneManager && window.SceneManager.setEditorMode) window.SceneManager.setEditorMode(true);
        if (orbitControls) orbitControls.enabled = false;
        btn.textContent = '❌ Exit Creator Mode';
        btn.classList.add('active');
        document.body.style.cursor = 'crosshair';
        sidebar.classList.add('active');

        // Show hotspots
        hotspotsArray.forEach(hp => {
            const icon = hp.children.find(c => c.name === 'hotspot_icon');
            if (icon) icon.material.opacity = 0.8;
        });

    } else {
        if (window.SceneManager && window.SceneManager.setEditorMode) window.SceneManager.setEditorMode(false);
        if (orbitControls) orbitControls.enabled = true;
        btn.textContent = '✏️ Enter Creator Mode';
        btn.classList.remove('active');
        document.body.style.cursor = 'default';
        closeCreatorModal();
        sidebar.classList.remove('active');

        // Hide hotspots & Detach
        hotspotsArray.forEach(hp => {
            const icon = hp.children.find(c => c.name === 'hotspot_icon');
            if (icon) icon.material.opacity = 0;
        });
        transformControls.detach();
    }
}

/**
 * Handle Clicks in Editor Mode
 */
export function handleEditorClick(event, model) {
    if (!isEditorActive || !CONFIG.settings.enableEditor) return false;

    // Ignore clicks on UI
    if (event.target.closest('.editor-toggle-container') ||
        event.target.closest('.creator-modal') ||
        event.target.closest('.editor-sidebar')) return true;

    // Calculate mouse position
    const mouse = new THREE.Vector2();
    mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
    mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;

    raycaster.setFromCamera(mouse, camera);

    // 1. Try Selecting Hotspot (Group)
    if (hotspotsArray) {
        // console.log('Checking intersections with hotspots:', hotspotsArray.length);
        const hotspotIntersects = raycaster.intersectObjects(hotspotsArray, true);
        if (hotspotIntersects.length > 0) {

            // Find the root group (parent of the hit object)
            let group = hotspotIntersects[0].object;
            while (group.parent && group.parent.type !== 'Scene' && !hotspotsArray.includes(group)) {
                group = group.parent;
            }

            if (hotspotsArray.includes(group)) {

                transformControls.attach(group);
                return true;
            } else {

            }
        } else {
            // console.log('No hotspot intersection');
        }
    }

    /* Creation Disabled
    const intersects = raycaster.intersectObject(model, true);

    if (intersects.length > 0) {
        const hit = intersects[0];
        capturedPosition = hit.point;
        openCreatorModal();
        return true;
    }
    */

    return false;
}

function openCreatorModal() {
    const count = hotspotsArray.length + newHotspots.length + 1;
    document.getElementById('new-hotspot-id').value = `Hotspot_${count}`;
    document.getElementById('new-hotspot-text').value = `Feature ${count}`;
    document.getElementById('creator-modal').classList.add('active');
}

function closeCreatorModal() {
    document.getElementById('creator-modal').classList.remove('active');
    capturedPosition = null;
}

/**
 * Save the new hotspot
 */
function saveNewHotspot() {
    if (!capturedPosition) return;

    const id = document.getElementById('new-hotspot-id').value || `Hotspot_${Date.now()}`;
    const text = document.getElementById('new-hotspot-text').value || 'New Point';
    const title = document.getElementById('new-hotspot-title').value || text;
    const desc = document.getElementById('new-hotspot-desc').value || 'No description provided.';

    const hotspotConfig = {
        name: id,
        position: {
            x: Math.round(capturedPosition.x),
            y: Math.round(capturedPosition.y),
            z: Math.round(capturedPosition.z)
        },
        text: text,
        color: CONFIG.theme.defaultHotspotColor || '#404040',
        visibleRange: { min: 0, max: 1000 }
    };

    const panelConfig = {
        title: title,
        description: desc,
        buttons: [{ label: 'Action', type: 'primary', action: null }],
        image: null
    };

    newHotspots.push(hotspotConfig);
    newPanels[id] = panelConfig;

    const event = new CustomEvent('create-hotspot', { detail: { config: hotspotConfig } });
    window.dispatchEvent(event);

    closeCreatorModal();
}

/**
 * Generate Export Code
 */
function showExportCode() {
    // Merge new data with original CONFIG
    const finalConfig = JSON.parse(JSON.stringify(CONFIG));

    // Update Theme
    finalConfig.theme.backgroundGradient.top = document.getElementById('edit-bg-top').value;
    finalConfig.theme.backgroundGradient.bottom = document.getElementById('edit-bg-bottom').value;
    finalConfig.theme.navBar.backgroundColor = document.getElementById('edit-nav-color').value;
    finalConfig.settings.showPlexus = document.getElementById('edit-show-plexus').checked;

    // Update Model settings (Camera might have changed)
    if (editConfig.model.cameraPosition) {
        finalConfig.model.cameraPosition = editConfig.model.cameraPosition;
    }

    // Merge Hotspots
    finalConfig.hotspots = [...finalConfig.hotspots, ...newHotspots];

    // Merge Modified Positions from Editor
    if (hotspotsArray) {
        hotspotsArray.forEach(hp => {
            const matchingConfig = finalConfig.hotspots.find(h => h.name === hp.name);
            if (matchingConfig) {
                matchingConfig.position = {
                    x: Math.round(hp.position.x),
                    y: Math.round(hp.position.y),
                    z: Math.round(hp.position.z)
                };
            }
        });
    }

    finalConfig.panels = { ...finalConfig.panels, ...newPanels };

    // Generate String
    const jsonStr = JSON.stringify(finalConfig, null, 4);

    // We want to make it a valid JS module export
    // We need to re-format the JSON to be a JS object (keys without quotes if possible, function placeholders)
    // For simplicity, we'll export the whole JSON object for now, 
    // but users can create a clean object structure.

    const output = `/**
 * ===================================================
 * INTERACTIVE EXPERIENCE CONFIGURATION
 * Generated by Creator Mode
 * ===================================================
 */

export const CONFIG = ${jsonStr};`;

    document.getElementById('export-code-area').value = output;
    document.getElementById('export-modal').classList.add('active');
}

function copyExportCode() {
    const code = document.getElementById('export-code-area');
    code.select();
    document.execCommand('copy');
    alert('📋 Full configuration copied!');
}
